import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Mail, CheckCircle2, ArrowRight } from "lucide-react";
import axios from "axios";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { LiveDonateDialog } from "./LiveDonateDialog";
import { trackBetaSignup } from "@/lib/analytics";

/**
 * DonateComingSoonDialog — live-surface Donate CTA target.
 *
 * Wraps two donation experiences and picks between them at render time:
 *   • REACT_APP_DONATIONS_LIVE=true  → real Stripe Checkout (LiveDonateDialog)
 *   • otherwise (default)            → warm "Giving opens soon" email capture
 *
 * The flag is a frontend build-time env var (CRA — set in .env and Republish
 * to change). The safety default is OFF so a fresh production deploy always
 * gates donations behind the email capture until an admin explicitly opens
 * them up.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DONATIONS_LIVE =
  String(process.env.REACT_APP_DONATIONS_LIVE || "").toLowerCase() === "true";

export const DonateComingSoonDialog = (props) => {
  // Delegate to real Stripe when donations are live. Same prop shape.
  if (DONATIONS_LIVE) {
    return <LiveDonateDialog {...props} />;
  }
  return <ComingSoonDialogInner {...props} />;
};

const ComingSoonDialogInner = ({ open, onOpenChange, studentName }) => {
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      // Reset on close so a returning donor sees the composer fresh.
      const t = setTimeout(() => {
        setEmail("");
        setTouched(false);
        setSubmitting(false);
        setSuccess(false);
        setError("");
      }, 300);
      return () => clearTimeout(t);
    }
    // Focus the email field for keyboard-first donors
    const t = setTimeout(() => inputRef.current?.focus(), 180);
    return () => clearTimeout(t);
  }, [open]);

  const emailValid = useMemo(() => EMAIL_RE.test(email.trim()), [email]);
  const showError = touched && !emailValid && email.length > 0;

  const submit = async (e) => {
    e?.preventDefault?.();
    setTouched(true);
    if (!emailValid || submitting) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/beta-signups`, {
        email: email.trim().toLowerCase(),
        role: "donor",
        source: studentName ? `donate_coming_soon:${studentName}` : "donate_coming_soon",
      });
      trackBetaSignup({
        role: "donor",
        source: studentName ? `donate_coming_soon:${studentName}` : "donate_coming_soon",
      });
      setSuccess(true);
    } catch (err) {
      // beta-signups is idempotent — a duplicate email still returns 200
      // in the current backend. Any 4xx/5xx here is a genuine failure.
      setError(err?.response?.data?.detail || "Something didn't send. Try again in a moment.");
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="donate-coming-soon-dialog"
        className="max-w-lg overflow-hidden border-border bg-sand p-0 sm:rounded-lg"
      >
        <div className="relative overflow-hidden bg-beige px-8 pb-8 pt-9">
          <span aria-hidden className="pointer-events-none absolute -right-10 -top-14 h-44 w-44 rounded-full bg-yellow/45 blur-3xl" />
          <span aria-hidden className="pointer-events-none absolute -left-8 -bottom-14 h-40 w-40 rounded-full bg-tangerine/25 blur-3xl" />

          <div className="relative flex items-center gap-2.5">
            <span className="inline-flex h-9 items-center rounded-full bg-ink px-3 text-[13px] font-semibold uppercase tracking-[0.22em] text-sand">
              Coming soon
            </span>
          </div>

          <DialogTitle className="relative mt-5 font-serif text-3xl lg:text-[3.5rem] font-normal leading-[1.02] tracking-tight text-ink">
            Giving opens soon.
          </DialogTitle>

          <DialogDescription className="relative mt-4 max-w-md text-base leading-relaxed text-olive">
            Drop your email below and you&rsquo;ll be one of the first to know.
          </DialogDescription>
        </div>

        <div className="px-8 pb-8 pt-6">
          <AnimatePresence mode="wait">
            {success ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                data-testid="donate-coming-soon-success"
                className="relative flex items-start gap-4 rounded-xl border border-grass/30 bg-grass/[0.06] p-5"
              >
                <span className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-grass text-sand">
                  <CheckCircle2 className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <div className="min-w-0 flex-1">
                  <p className="font-serif text-3xl text-ink">You&rsquo;re on the list.</p>
                  <p className="mt-1 text-base leading-relaxed text-olive">
                    We&rsquo;ll email <span className="font-semibold text-ink">{email.trim().toLowerCase()}</span> the moment giving opens.
                  </p>
                  <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    data-testid="donate-coming-soon-close"
                    className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-ink px-5 py-2 text-[13px] font-semibold uppercase tracking-wide text-sand transition hover:bg-forest"
                  >
                    Back to the campaign
                  </button>
                </div>
              </motion.div>
            ) : (
              <motion.form
                key="composer"
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
                onSubmit={submit}
                className="relative"
                data-testid="donate-coming-soon-form"
              >
                <label htmlFor="donate-coming-soon-email" className="text-[13px] font-semibold uppercase tracking-[0.22em] text-olive">
                  Notify me
                </label>
                <div className="relative mt-2">
                  <span aria-hidden className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-olive/60">
                    <Mail className="h-4 w-4" strokeWidth={1.75} />
                  </span>
                  <input
                    ref={inputRef}
                    id="donate-coming-soon-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched(true)}
                    disabled={submitting}
                    data-testid="donate-coming-soon-email"
                    className={`block w-full rounded-full border-2 bg-bone px-11 py-3.5 text-base text-ink placeholder:text-ink/30 focus:outline-none focus:ring-4 focus:ring-ink/10 ${ showError ? "border-scarlet" : "border-border focus:border-ink" }`}
                    required
                  />
                </div>
                {showError && (
                  <p role="alert" data-testid="donate-coming-soon-email-error" className="mt-2 text-[13px] text-scarlet">
                    Enter a valid email so we can reach you.
                  </p>
                )}
                {error && (
                  <p role="alert" data-testid="donate-coming-soon-form-error" className="mt-2 rounded-md bg-scarlet/8 px-3 py-2 text-[13px] text-scarlet">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={submitting || !emailValid}
                  data-testid="donate-coming-soon-submit"
                  className="mt-4 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-base font-semibold text-sand transition-colors hover:bg-forest disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {submitting ? (
                    <><Loader2 className="h-4 w-4 animate-spin" /> Adding you…</>
                  ) : (
                    <>Notify me when donations open <ArrowRight className="h-4 w-4" strokeWidth={2} /></>
                  )}
                </button>

                <p className="mt-4 text-center text-[13px] leading-relaxed text-olive">
                  My Rich Aunt is a registered 501(c)(3) nonprofit. Every dollar you eventually give here is 100% tax-deductible.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  );
};
